import { Link, useNavigate, useLocation } from "react-router-dom"
import { useEffect, useRef, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { useAuthStore } from "@/store/authStore"
import { authService } from "@/services/authService"
import { notificationService } from "@/services/notificationService"
import { TwoFactorSetupModal } from "@/components/TwoFactorSetupModal"
import { TermsUpdateDialog } from "@/components/TermsUpdateDialog"
import { Logo } from "@/components/layout/Logo"
import { WalletSwitcher, WalletReadOnlyBanner } from "@/components/layout/WalletSwitcher"
import { useTheme } from "@/hooks/useTheme"
import { PLAN_LABEL, effectivePlanOf, hasProPlan } from "@/lib/planNames"
import {
  LayoutGrid, LineChart, ArrowLeftRight, Landmark, CreditCard, Repeat, Download,
  PieChart, Target, Scale, TrendingUp, MessageSquare, Sparkles, Calculator, Tags, Wand2,
  Bell, Settings, HelpCircle, ShieldCheck, Zap, LogOut, Sun, Moon, Menu, X, Lock, MailWarning,
  MoreHorizontal, ChevronsUpDown, Users,
} from "lucide-react"
import { toast } from "sonner"

interface NavItemDef { href: string; icon: React.ElementType; label: string; pro: boolean }

// ── Navegação agrupada pelo que o usuário quer fazer ──────────────────────
const NAV_GROUPS: { label: string; items: NavItemDef[] }[] = [
  {
    label: "Visão geral",
    items: [
      { href: "/dashboard", icon: LayoutGrid, label: "Painel",     pro: false },
      { href: "/reports",   icon: LineChart,  label: "Relatórios", pro: false },
      { href: "/group",     icon: Users,      label: "Grupo",      pro: false },
    ],
  },
  {
    label: "Movimentações",
    items: [
      { href: "/transactions",  icon: ArrowLeftRight, label: "Transações",   pro: false },
      { href: "/bank-accounts", icon: Landmark,       label: "Contas",       pro: false },
      { href: "/credit-cards",  icon: CreditCard,     label: "Cartões",      pro: false },
      { href: "/recurring",     icon: Repeat,         label: "Recorrências", pro: true  },
      { href: "/imports",       icon: Download,       label: "Importações",  pro: true  },
    ],
  },
  {
    label: "Planejamento",
    items: [
      { href: "/budgets", icon: PieChart, label: "Orçamentos", pro: true },
      { href: "/goals",   icon: Target,   label: "Metas",      pro: true },
      { href: "/debts",   icon: Scale,    label: "Dívidas",    pro: true },
    ],
  },
  {
    label: "Patrimônio",
    items: [
      { href: "/investments", icon: TrendingUp, label: "Investimentos", pro: true },
    ],
  },
  {
    label: "Ferramentas",
    items: [
      { href: "/chat",        icon: MessageSquare, label: "FinBot",             pro: false },
      { href: "/ai-analysis", icon: Sparkles,      label: "Análise com IA",     pro: true  },
      { href: "/calculators", icon: Calculator,    label: "Calculadoras",       pro: true  },
      { href: "/categories",  icon: Tags,          label: "Categorias",         pro: false },
      { href: "/auto-rules",  icon: Wand2,         label: "Regras automáticas", pro: true  },
    ],
  },
]

const BOTTOM_NAV: NavItemDef[] = [
  { href: "/dashboard",     icon: LayoutGrid,     label: "Painel",     pro: false },
  { href: "/transactions",  icon: ArrowLeftRight, label: "Transações", pro: false },
  { href: "/bank-accounts", icon: Landmark,       label: "Contas",     pro: false },
  { href: "/reports",       icon: LineChart,      label: "Relatórios", pro: false },
]

function initials(name?: string) {
  if (!name) return "?"
  const parts = name.trim().split(/\s+/)
  return ((parts[0]?.[0] ?? "") + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase()
}

function EmailVerificationBanner({ email }: { email: string }) {
  const [sending, setSending] = useState(false)

  const resend = async () => {
    setSending(true)
    try {
      await authService.resendVerification(email)
      toast.success("E-mail de verificação enviado!")
    } catch {
      toast.error("Erro ao reenviar. Tente novamente.")
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="shrink-0 bg-warning-50 dark:bg-warning-950/40 border-b border-warning-200 dark:border-warning-900 px-4 md:px-8 py-2.5 flex items-center gap-3 text-[13px]">
      <MailWarning className="h-4 w-4 text-warning-700 dark:text-warning-400 shrink-0" />
      <span className="text-warning-800 dark:text-warning-300 flex-1">
        Confirme seu e-mail para liberar todos os recursos.
      </span>
      <button
        onClick={resend}
        disabled={sending}
        className="text-warning-800 dark:text-warning-300 underline underline-offset-2 font-medium disabled:opacity-50 shrink-0"
      >
        {sending ? "Enviando..." : "Reenviar e-mail"}
      </button>
    </div>
  )
}

interface AppLayoutProps { children: React.ReactNode }

export function AppLayout({ children }: AppLayoutProps) {
  const navigate  = useNavigate()
  const location  = useLocation()
  const { user, setAuth, logout } = useAuthStore()
  const { theme, toggle } = useTheme()
  const [drawerOpen, setDrawerOpen]   = useState(false)
  const [accountOpen, setAccountOpen] = useState(false)
  const [twoFaOpen,  setTwoFaOpen]    = useState(false)
  const accountRef = useRef<HTMLDivElement>(null)

  const { data: notifications = [] } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => notificationService.getAll(),
    refetchInterval: 60_000,
  })
  const unreadCount = notifications.filter(n => !n.isRead).length
  const isPro = hasProPlan(user)
  const planLabel = `Plano ${PLAN_LABEL[effectivePlanOf(user)]}`

  // Fecha a gaveta e o menu da conta ao trocar de página (ajuste de estado durante o render)
  const [lastPath, setLastPath] = useState(location.pathname)
  if (lastPath !== location.pathname) {
    setLastPath(location.pathname)
    setDrawerOpen(false)
    setAccountOpen(false)
  }

  // Fecha o menu da conta ao clicar fora ou apertar Esc
  useEffect(() => {
    if (!accountOpen) return
    const onClick = (e: MouseEvent) => {
      if (accountRef.current && !accountRef.current.contains(e.target as Node)) setAccountOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setAccountOpen(false) }
    document.addEventListener("mousedown", onClick)
    document.addEventListener("keydown", onKey)
    return () => { document.removeEventListener("mousedown", onClick); document.removeEventListener("keydown", onKey) }
  }, [accountOpen])

  const handleLogout = async () => {
    await authService.logout()
    logout()
    navigate("/login")
  }

  const handleTwoFaSuccess = async () => {
    try { const updated = await authService.me(); setAuth(updated) } catch { /**/ }
  }

  const isActive = (href: string) => location.pathname === href

  const renderNavItem = ({ href, icon: Icon, label, pro }: NavItemDef) => {
    const locked = pro && !isPro
    const active = !locked && isActive(href)
    return (
      <Link
        key={href}
        to={locked ? "/pricing" : href}
        aria-current={active ? "page" : undefined}
        title={locked ? "Disponível no plano Pro" : undefined}
        className={`flex items-center gap-2.5 px-2.5 h-9 rounded-md text-[14px] transition-colors
          ${active
            ? "bg-positive-50 text-positive-800 font-medium dark:bg-positive-950/60 dark:text-positive-200"
            : locked
              ? "text-muted-foreground hover:bg-inset"
              : "text-foreground/80 hover:bg-inset hover:text-foreground"}`}
      >
        <Icon className="h-4 w-4 shrink-0" strokeWidth={1.8} />
        <span className="truncate">{label}</span>
        {locked && <Lock className="ml-auto h-3 w-3 shrink-0" aria-label="Plano Pro" />}
      </Link>
    )
  }

  const navigation = (
    <>
    <WalletSwitcher />
    <nav aria-label="Navegação principal" className="flex-1 overflow-y-auto px-3 py-5 space-y-6">
      {NAV_GROUPS.map(group => (
        <div key={group.label}>
          <p className="px-2.5 mb-1.5 text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground">
            {group.label}
          </p>
          <div className="space-y-0.5">{group.items.map(renderNavItem)}</div>
        </div>
      ))}
    </nav>
    </>
  )

  const itemClass = "w-full flex items-center gap-2.5 px-3 h-9 rounded-md text-[14px] hover:bg-inset text-left"
  const iconClass = "h-4 w-4 text-muted-foreground shrink-0"

  const accountMenuItems = (
    <>
      <Link to="/notifications" className={itemClass}>
        <Bell className={iconClass} strokeWidth={1.8} /> Notificações
        {unreadCount > 0 && (
          <span className="ml-auto text-[11px] font-medium rounded bg-warning-100 text-warning-800 dark:bg-warning-950 dark:text-warning-300 px-1.5 py-0.5 num">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </Link>
      <Link to="/settings" className={itemClass}>
        <Settings className={iconClass} strokeWidth={1.8} /> Configurações
      </Link>
      <button onClick={() => { setTwoFaOpen(true); setAccountOpen(false); setDrawerOpen(false) }} className={itemClass}>
        <ShieldCheck className={iconClass} strokeWidth={1.8} />
        {user?.twoFactorEnabled ? "Verificação em duas etapas" : "Ativar verificação em duas etapas"}
      </button>
      <Link to="/pricing" className={itemClass}>
        <Zap className={iconClass} strokeWidth={1.8} /> {isPro ? planLabel : "Conhecer o Pro"}
      </Link>
      <Link to="/help" className={itemClass}>
        <HelpCircle className={iconClass} strokeWidth={1.8} /> Ajuda
      </Link>
      <button onClick={toggle} className={itemClass}>
        {theme === "dark"
          ? <Sun className={iconClass} strokeWidth={1.8} />
          : <Moon className={iconClass} strokeWidth={1.8} />}
        {theme === "dark" ? "Tema claro" : "Tema escuro"}
      </button>
      <div className="my-1 border-t" />
      <button onClick={handleLogout} className={itemClass}>
        <LogOut className={iconClass} strokeWidth={1.8} /> Sair
      </button>
    </>
  )

  const userBadge = (
    <>
      <span className="h-8 w-8 rounded-full bg-positive-100 text-positive-800 dark:bg-positive-900 dark:text-positive-100 text-[12px] font-semibold flex items-center justify-center shrink-0">
        {initials(user?.name)}
      </span>
      <span className="min-w-0 text-left">
        <span className="block text-[13px] font-medium truncate">{user?.name}</span>
        <span className="block text-[12px] text-muted-foreground truncate">{planLabel}</span>
      </span>
    </>
  )

  return (
    <div className="flex h-dvh bg-background overflow-hidden">

      {/* ── Menu lateral (desktop) ────────────────────────────────────── */}
      <aside className="hidden lg:flex w-[248px] border-r bg-card flex-col shrink-0">
        <div className="h-16 pl-5 pr-3 flex items-center justify-between border-b">
          <Link to="/dashboard" aria-label="FinControl, ir para o painel"><Logo /></Link>
          <Link to="/notifications" aria-label={unreadCount > 0 ? `Notificações, ${unreadCount} não lidas` : "Notificações"}
            className={`relative h-9 w-9 flex items-center justify-center rounded-md hover:bg-inset
              ${isActive("/notifications") ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}>
            <Bell className="h-[18px] w-[18px]" strokeWidth={1.8} />
            {unreadCount > 0 && <span className="absolute top-2 right-2 h-2 w-2 rounded-full bg-warning-600" />}
          </Link>
        </div>

        {navigation}

        <div className="border-t p-3 relative" ref={accountRef}>
          {accountOpen && (
            <div role="menu" className="absolute bottom-full left-3 right-3 mb-2 rounded-lg border bg-card shadow-overlay p-1.5 z-50">
              {accountMenuItems}
            </div>
          )}
          <button
            onClick={() => setAccountOpen(o => !o)}
            aria-expanded={accountOpen}
            aria-haspopup="menu"
            aria-label="Menu da conta"
            className="w-full flex items-center gap-3 rounded-md px-2.5 py-2 hover:bg-inset"
          >
            {userBadge}
            <span className="ml-auto flex items-center gap-2 text-muted-foreground">
              {unreadCount > 0 && <span className="h-2 w-2 rounded-full bg-warning-600" />}
              <ChevronsUpDown className="h-4 w-4" />
            </span>
          </button>
        </div>
      </aside>

      {/* ── Gaveta (celular e tablet) ─────────────────────────────────── */}
      {drawerOpen && (
        <div className="fixed inset-0 z-40 bg-neutral-950/40 lg:hidden" onClick={() => setDrawerOpen(false)} />
      )}
      <aside
        aria-label="Menu"
        className={`fixed inset-y-0 left-0 z-50 w-[288px] max-w-[85vw] bg-card flex flex-col shadow-overlay transition-transform duration-200 lg:hidden
          ${drawerOpen ? "translate-x-0" : "-translate-x-full invisible"}`}
      >
        <div className="flex items-center justify-between px-5 h-14 border-b shrink-0">
          <Logo />
          <button onClick={() => setDrawerOpen(false)} aria-label="Fechar menu"
            className="h-9 w-9 -mr-2 flex items-center justify-center rounded-md hover:bg-inset text-muted-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto flex flex-col">
          {navigation}
          <div className="border-t p-3 space-y-0.5">
            <div className="flex items-center gap-3 px-2.5 py-2">{userBadge}</div>
            {accountMenuItems}
          </div>
        </div>
      </aside>

      {/* ── Coluna principal ──────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">

        {/* Barra superior (celular e tablet) */}
        <header className="lg:hidden flex items-center justify-between px-2 h-14 border-b bg-card shrink-0">
          <button onClick={() => setDrawerOpen(true)} aria-label="Abrir menu" aria-expanded={drawerOpen}
            className="h-10 w-10 flex items-center justify-center rounded-md hover:bg-inset text-foreground/80">
            <Menu className="h-5 w-5" />
          </button>
          <Link to="/dashboard" aria-label="FinControl, ir para o painel"><Logo /></Link>
          <Link to="/notifications" aria-label={unreadCount > 0 ? `Notificações, ${unreadCount} não lidas` : "Notificações"}
            className="relative h-10 w-10 flex items-center justify-center rounded-md hover:bg-inset text-foreground/80">
            <Bell className="h-5 w-5" strokeWidth={1.8} />
            {unreadCount > 0 && <span className="absolute top-2 right-2 h-2 w-2 rounded-full bg-warning-600" />}
          </Link>
        </header>

        {user && !user.emailVerified && <EmailVerificationBanner email={user.email} />}
        <WalletReadOnlyBanner />

        <main id="conteudo" className="flex-1 overflow-y-auto pb-20 lg:pb-0">
          {children}
        </main>
      </div>

      {/* ── Navegação inferior (celular e tablet) ─────────────────────── */}
      <nav aria-label="Atalhos" className="fixed bottom-0 inset-x-0 z-30 lg:hidden bg-card border-t safe-area-pb">
        <div className="grid grid-cols-5 h-16">
          {BOTTOM_NAV.map(({ href, icon: Icon, label }) => {
            const active = isActive(href)
            return (
              <Link key={href} to={href} aria-current={active ? "page" : undefined}
                className={`flex flex-col items-center justify-center gap-1 ${active ? "text-primary" : "text-muted-foreground"}`}>
                <Icon className="h-5 w-5" strokeWidth={active ? 2.2 : 1.8} />
                <span className="text-[11px] font-medium">{label}</span>
              </Link>
            )
          })}
          <button onClick={() => setDrawerOpen(true)} className="flex flex-col items-center justify-center gap-1 text-muted-foreground">
            <MoreHorizontal className="h-5 w-5" strokeWidth={1.8} />
            <span className="text-[11px] font-medium">Mais</span>
          </button>
        </div>
      </nav>

      <TwoFactorSetupModal open={twoFaOpen} onClose={() => setTwoFaOpen(false)} onSuccess={handleTwoFaSuccess} />
      <TermsUpdateDialog />
    </div>
  )
}
